import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import Checkbox from '@material-ui/core/Checkbox';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import useRelevantPolicies from 'navbar/src/jupiterAccountSettings/hooks/useRelevantPolicies';
import { PolicyRule } from 'navbar/src/jupiterAccountSettings/types';

interface Props {
  groupId: string;
}

interface App {
  title: string;
  adminRule: PolicyRule;
  accessRule: PolicyRule;
}

const appPermissionsIndex: App[] = [
  {
    title: 'All Apps and Resources',
    adminRule: PolicyRule.FullAdmin,
    accessRule: PolicyRule.FullRead,
  },
  {
    title: 'Shared: Questions',
    adminRule: PolicyRule.WriteQuestions,
    accessRule: PolicyRule.ReadQuestions,
  },
  {
    title: 'Shared: Graph Data',
    adminRule: PolicyRule.WriteGraph,
    accessRule: PolicyRule.ReadGraph,
  },
  {
    title: 'Landing',
    adminRule: PolicyRule.LandingAdmin,
    accessRule: PolicyRule.LandingAccess,
  },
  {
    title: 'My Security',
    adminRule: PolicyRule.MySecurityAdmin,
    accessRule: PolicyRule.MySecurityAccess,
  },
  {
    title: 'Assets',
    adminRule: PolicyRule.AssetsAdmin,
    accessRule: PolicyRule.AssetsAccess,
  },
  {
    title: 'Policies',
    adminRule: PolicyRule.PoliciesAdmin,
    accessRule: PolicyRule.PoliciesAccess,
  },
  {
    title: 'Compliance',
    adminRule: PolicyRule.ComplianceAdmin,
    accessRule: PolicyRule.ComplianceAccess,
  },
  {
    title: 'Alerts',
    adminRule: PolicyRule.RulesAdmin,
    accessRule: PolicyRule.RulesAccess,
  },
  {
    title: 'GraphViewer',
    adminRule: PolicyRule.GalaxyAdmin,
    accessRule: PolicyRule.GalaxyAccess,
  },
  {
    title: 'Insights',
    adminRule: PolicyRule.InsightsAdmin,
    accessRule: PolicyRule.InsightsAccess,
  },
  {
    title: 'Integrations',
    adminRule: PolicyRule.IntegrationsAdmin,
    accessRule: PolicyRule.IntegrationsAccess,
  },
  {
    title: 'Endpoint Compliance (Powerup)',
    adminRule: PolicyRule.EndpointComplianceAdmin,
    accessRule: PolicyRule.EndpointComplianceAccess,
  },
];

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    checkboxCell: {
      textAlign: 'center',
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: '4px 48px 24px',
      color: theme.palette.common.white,
    },
    title: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(),
      textAlign: 'center',
    },
  })
);

const AppPermissions = ({ groupId }: Props) => {
  const classes = useStyles();
  const { getGroupPolicyRule, togglePolicyRule } = useRelevantPolicies();
  const policyEnabled = (rule: PolicyRule) => getGroupPolicyRule(groupId, rule);
  const toggleRule = (rule: PolicyRule) => async () => {
    await togglePolicyRule(groupId, rule);
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="subtitle1" color="primary">
        Manage App Permissions
      </Typography>
      <table>
        <tbody>
          <tr>
            <th />
            <th>Admin</th>
            <th>Read-only</th>
          </tr>
          {appPermissionsIndex.map(({ title, adminRule, accessRule }) => (
            <tr key={title}>
              <th>{title}</th>
              <td className={classes.checkboxCell}>
                <Checkbox
                  color="primary"
                  checked={policyEnabled(adminRule)}
                  onChange={toggleRule(adminRule)}
                />
              </td>
              <td className={classes.checkboxCell}>
                <Checkbox
                  color="primary"
                  checked={policyEnabled(accessRule)}
                  onChange={toggleRule(accessRule)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AppPermissions;
